import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiSend } from '../utils/api';

const STAGES = ['Available', 'Tagged', 'Offer Sent', 'Under Contract', 'Sold', 'Closed', 'Dropped', 'Dead'];
const TYPES = [{ v: 'assignment', l: 'Assignment' }, { v: 'double_close', l: 'Double Close' }, { v: 'jv', l: 'JV' }];

// Mirrors the server rules in routes/deals.js (POST /).
const REQUIRED_BASE = ['address', 'deal_type', 'close_date', 'earnest_money', 'title_company', 'acquisitions_agent'];
const REQUIRED_SOLD = ['sale_price', 'fee', 'buyer'];
const REQUIRED_CLOSED = [...REQUIRED_SOLD, 'dispositions_agent'];

const card = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: 20, marginBottom: 16 };
const sectionTitle = { fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 };
const label = { display: 'block', fontSize: 12, color: 'var(--gray-300)', marginBottom: 5 };
const input = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
const btnPrimary = { padding: '9px 18px', borderRadius: 8, background: 'var(--teal)', border: '1px solid var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const btnGhost = { padding: '9px 16px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 13, fontWeight: 700, cursor: 'pointer' };

function Field({ id, label: lab, required, children }) {
  return (
    <div>
      <label htmlFor={id} style={label}>{lab}{required && <span style={{ color: '#B85450' }}> *</span>}</label>
      {children}
    </div>
  );
}

export default function AddDeal() {
  const navigate = useNavigate();
  const [f, setF] = useState({ stage: 'Available' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [dup, setDup] = useState(null);   // existing deal id when the property is already in the pipeline

  const set = (k) => (e) => setF(prev => ({ ...prev, [k]: e.target.value }));
  const isSold = f.stage === 'Sold';
  const isClosed = f.stage === 'Closed';

  const required = useMemo(() => {
    if (isClosed) return [...REQUIRED_BASE, ...REQUIRED_CLOSED];
    if (isSold) return [...REQUIRED_BASE, ...REQUIRED_SOLD];
    return REQUIRED_BASE;
  }, [isSold, isClosed]);
  const req = (name) => required.includes(name);

  async function submit() {
    setError(null);
    const missing = required.filter(k => !f[k] || String(f[k]).trim() === '');
    if (missing.length) { setError(`Please fill the required field(s): ${missing.join(', ')}`); return; }
    setBusy(true);
    try {
      const r = await apiSend('/api/deals', { method: 'POST', body: f });
      setDone(r);
    } catch (e) {
      // Duplicate property → offer to jump to the existing deal instead of creating a second.
      if (e.httpStatus === 409 && e.payload?.existing_deal_id) {
        setDup(e.payload.existing_deal_id);
      } else {
        setError(e.message || 'Could not create the deal.');
      }
    }
    setBusy(false);
  }

  if (dup) {
    return (
      <div style={{ maxWidth: 720 }}>
        <div style={{ ...card, marginTop: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--gold)', marginBottom: 8 }}>This property is already in your pipeline</div>
          <div style={{ fontSize: 14, color: 'var(--gray-300)', marginBottom: 18 }}>
            A deal for <strong style={{ color: 'var(--white)' }}>{f.address}</strong> already exists. We didn't create a duplicate.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => navigate(`/pipeline/deal/${dup}`)} style={btnPrimary}>Go to that deal</button>
            <button onClick={() => { setDup(null); }} style={btnGhost}>Edit what I entered</button>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={{ maxWidth: 720 }}>
        <div style={{ ...card, marginTop: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#4A7C59', marginBottom: 8 }}>✓ Deal created</div>
          <div style={{ fontSize: 14, color: 'var(--gray-300)', marginBottom: 18 }}>
            Added <strong style={{ color: 'var(--white)' }}>{f.address}</strong> at stage <strong style={{ color: 'var(--white)' }}>{done.stage}</strong>.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => navigate('/pipeline')} style={btnPrimary}>Go to pipeline</button>
            <button onClick={() => { setDone(null); setF({ stage: 'Available' }); }} style={btnGhost}>Add another</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.02em', marginBottom: 6 }}>Add Deal</h1>
          <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>Manually add a single deal. Saving never sends email or SMS.</p>
        </div>
        <button onClick={() => navigate('/pipeline')} style={btnGhost}>← Pipeline</button>
      </div>

      {error && (
        <div style={{ ...card, padding: 14, borderColor: '#B85450', background: 'rgba(184,84,80,0.08)', color: '#B85450', fontSize: 13 }}>{error}</div>
      )}

      {/* Stage */}
      <div style={card}>
        <div style={sectionTitle}>Stage</div>
        <div style={{ maxWidth: 280 }}>
          <Field id="stage" label="Pipeline stage">
            <select id="stage" value={f.stage} onChange={set('stage')} style={input}>
              {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>
        {(isSold || isClosed) && (
          <div style={{ fontSize: 12, color: 'var(--gold)', marginTop: 8 }}>
            {isClosed ? 'Closed deals require sale price, fee, buyer, and dispositions agent.' : 'Sold deals require sale price, fee, and buyer.'}
          </div>
        )}
      </div>

      {/* Required basics */}
      <div style={card}>
        <div style={sectionTitle}>Deal basics</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field id="address" label="Address" required={req('address')}>
              <input id="address" value={f.address || ''} onChange={set('address')} style={input} placeholder="123 Pearl St" />
            </Field>
          </div>
          <Field id="zip" label="Zip">
            <input id="zip" value={f.zip || ''} onChange={set('zip')} style={input} placeholder="80302" />
          </Field>
          <Field id="deal_type" label="Type" required={req('deal_type')}>
            <select id="deal_type" value={f.deal_type || ''} onChange={set('deal_type')} style={input}>
              <option value="">Select…</option>
              {TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </Field>
          <Field id="close_date" label="Close date" required={req('close_date')}>
            <input id="close_date" type="date" value={f.close_date || ''} onChange={set('close_date')} style={input} />
          </Field>
          <Field id="earnest_money" label="Earnest money" required={req('earnest_money')}>
            <input id="earnest_money" type="number" value={f.earnest_money || ''} onChange={set('earnest_money')} style={input} placeholder="1000" />
          </Field>
          <Field id="title_company" label="Title company" required={req('title_company')}>
            <input id="title_company" value={f.title_company || ''} onChange={set('title_company')} style={input} />
          </Field>
          <Field id="acquisitions_agent" label="Acquisitions agent" required={req('acquisitions_agent')}>
            <input id="acquisitions_agent" value={f.acquisitions_agent || ''} onChange={set('acquisitions_agent')} style={input} />
          </Field>
        </div>
      </div>

      {/* Active-deal details */}
      <div style={card}>
        <div style={sectionTitle}>Active-deal details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field id="asking_price" label="Asking price">
            <input id="asking_price" type="number" value={f.asking_price || ''} onChange={set('asking_price')} style={input} />
          </Field>
          <Field id="arv" label="ARV">
            <input id="arv" type="number" value={f.arv || ''} onChange={set('arv')} style={input} />
          </Field>
          <Field id="close_of_escrow" label="Close of escrow (display, e.g. “ASAP”)">
            <input id="close_of_escrow" value={f.close_of_escrow || ''} onChange={set('close_of_escrow')} style={input} />
          </Field>
          <Field id="photos_link" label="Photos link">
            <input id="photos_link" value={f.photos_link || ''} onChange={set('photos_link')} style={input} />
          </Field>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field id="summary" label="Deal summary">
              <textarea id="summary" value={f.summary || ''} onChange={set('summary')} rows={2} style={{ ...input, resize: 'vertical' }} />
            </Field>
          </div>
        </div>
      </div>

      {/* Sold / Closed details (conditional) */}
      {(isSold || isClosed) && (
        <div style={card}>
          <div style={sectionTitle}>{f.stage} details</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field id="sale_price" label="Sale price (volume)" required={req('sale_price')}>
              <input id="sale_price" type="number" value={f.sale_price || ''} onChange={set('sale_price')} style={input} />
            </Field>
            <Field id="fee" label="Fee (net for JV)" required={req('fee')}>
              <input id="fee" type="number" value={f.fee || ''} onChange={set('fee')} style={input} />
            </Field>
            <Field id="buyer" label="Buyer" required={req('buyer')}>
              <input id="buyer" value={f.buyer || ''} onChange={set('buyer')} style={input} />
            </Field>
            {isClosed && (
              <Field id="dispositions_agent" label="Dispositions agent" required={req('dispositions_agent')}>
                <input id="dispositions_agent" value={f.dispositions_agent || ''} onChange={set('dispositions_agent')} style={input} />
              </Field>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={submit} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.5 : 1 }}>{busy ? 'Saving…' : 'Create deal'}</button>
        <button onClick={() => navigate('/pipeline')} style={btnGhost}>Cancel</button>
      </div>
    </div>
  );
}
